import { offlineDb } from "./db";

/**
 * Injecte des donnees de demonstration dans IndexedDB.
 * Utile pour tester l'UI sans saisie manuelle.
 */
export async function seedDemoData() {
  // M8 : jamais en prod. Les sites de démo sont `syncStatus: "pending"` / `userId: "local"`
  // → ils seraient poussés au serveur à la prochaine sync (pollution prod).
  if (!import.meta.env.DEV) {
    throw new Error("Les données de démo sont réservées au développement.");
  }

  // Verifier si des donnees existent deja
  const existingSites = await offlineDb.sites.count();
  if (existingSites > 0) {
    throw new Error("Des données existent déjà. Videz la base avant de recharger.");
  }

  const now = new Date();

  // IDs aléatoires pour éviter les conflits d'ownership en BDD
  // (des IDs fixes comme "site-demo-1" causent un bug : si un autre
  // utilisateur les a déjà pushés, l'upsert UPDATE ne change pas le userId)
  const siteId1 = crypto.randomUUID();
  const siteId2 = crypto.randomUUID();
  const siteId3 = crypto.randomUUID();

  // ─── Sites ───
  const sites = [
    {
      id: siteId1,
      name: "Réseau AEP Lyon 3e",
      client: "Veolia Eau",
      address: "12 rue de la République, 69003 Lyon",
      userId: "local",
      createdAt: now,
      updatedAt: now,
      syncStatus: "pending" as const,
    },
    {
      id: siteId2,
      name: "Station épuration Villeurbanne",
      client: "Métropole de Lyon",
      address: "45 avenue des Frères Lumière, 69100 Villeurbanne",
      userId: "local",
      createdAt: now,
      updatedAt: now,
      syncStatus: "pending" as const,
    },
    {
      id: siteId3,
      name: "Réseau incendie CHU Grenoble",
      client: "CHU Grenoble Alpes",
      address: "Boulevard de la Chantourne, 38700 La Tronche",
      userId: "local",
      createdAt: now,
      updatedAt: now,
      syncStatus: "pending" as const,
    },
  ];

  // ─── Zones ───
  const zones = [
    // Site Lyon
    {
      id: crypto.randomUUID(),
      name: "Sous-sol parking A",
      planType: "default",
      siteId: siteId1,
      createdAt: now,
      updatedAt: now,
      syncStatus: "pending" as const,
    },
    {
      id: crypto.randomUUID(),
      name: "Galerie technique niveau -1",
      planType: "default",
      siteId: siteId1,
      createdAt: now,
      updatedAt: now,
      syncStatus: "pending" as const,
    },
    {
      id: crypto.randomUUID(),
      name: "Local compteurs RDC",
      planType: "default",
      siteId: siteId1,
      createdAt: now,
      updatedAt: now,
      syncStatus: "pending" as const,
    },
    // Site Villeurbanne
    {
      id: crypto.randomUUID(),
      name: "Bassin de décantation",
      planType: "default",
      siteId: siteId2,
      createdAt: now,
      updatedAt: now,
      syncStatus: "pending" as const,
    },
    {
      id: crypto.randomUUID(),
      name: "Salle des pompes",
      planType: "default",
      siteId: siteId2,
      createdAt: now,
      updatedAt: now,
      syncStatus: "pending" as const,
    },
    // Site Grenoble
    {
      id: crypto.randomUUID(),
      name: "Bâtiment principal - sous-sol",
      planType: "default",
      siteId: siteId3,
      createdAt: now,
      updatedAt: now,
      syncStatus: "pending" as const,
    },
  ];

  // Inserer en bulk
  await offlineDb.sites.bulkAdd(sites);
  await offlineDb.zones.bulkAdd(zones);

  return { sites: sites.length, zones: zones.length };
}

/**
 * Supprime toutes les données LOCALES uniquement (IndexedDB).
 * Ne touche PAS au serveur (les données seront re-téléchargées
 * à la prochaine synchronisation).
 */
export async function clearAllData() {
  await offlineDb.pendingDeletions.clear();
  await offlineDb.photos.clear();
  await offlineDb.mesures.clear();
  await offlineDb.pins.clear();
  await offlineDb.campagnes.clear();
  await offlineDb.rapports.clear();
  await offlineDb.rapportAnnexes.clear();
  await offlineDb.zones.clear();
  await offlineDb.sites.clear();
  // Tables v13/v14 : sans ça, la librairie et les annexes d'un compte restent
  // visibles après changement de compte sur un appareil partagé (M8).
  await offlineDb.librarySections.clear();
  // Reset lastSyncAt : sans ça, la prochaine sync est un delta vide
  // au lieu d'un full pull, et Dexie reste vide pour toujours.
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem("ultra-mesure-last-sync-v2");
  }
}
